import {Dialog, Portal, Button, HStack, Icon, Text, VStack, Box} from '@chakra-ui/react'
import {FiLock, FiTrendingUp} from 'react-icons/fi'
import {useNavigate} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {useAppDispatch, useAppSelector} from '../../hooks/store'
import {useDialogBodyCleanup} from '../../hooks/useDialogBodyCleanup'
import {closeLimitGate, selectLimitGate, type GatePayload} from '../../slices/limitGateSlice'

/**
 * Global plan-limit modal. Mounted once at App root; opened via
 * `dispatch(openLimitGate(payload))` from anywhere (FE proactive checks,
 * 402 interceptor, useCapability().require()).
 *
 * The Dialog stays mounted across open/close — never returns null mid-close —
 * because unmounting before Chakra's close animation finishes strands
 * `body[pointer-events:none]` and freezes the page. A safety effect strips
 * Chakra's lingering body styles when `open` transitions to false, as a
 * belt-and-braces guard against that exact failure mode.
 */
export function LimitGate() {
    const {t} = useTranslation()
    const navigate = useNavigate()
    const dispatch = useAppDispatch()
    const {open, payload} = useAppSelector(selectLimitGate)

    // Safety against Chakra's stranded body styles after close. See hook docs.
    useDialogBodyCleanup(open)

    const close = () => dispatch(closeLimitGate())
    const upgrade = () => {
        close()
        // Defer the route change one tick so Chakra's close animation can run
        // against a stable component tree; navigating synchronously can race
        // with the Dialog teardown and strand body styles.
        window.setTimeout(() => navigate('/barber/settings#billing'), 0)
    }

    return (
        <Dialog.Root open={open && !!payload} onOpenChange={(e) => { if (!e.open) close() }}>
            <Portal>
                <Dialog.Backdrop />
                <Dialog.Positioner>
                    <Dialog.Content maxW="440px">
                        <Dialog.Body p={6}>
                            {payload && (
                            <VStack gap={4} align="stretch">
                                <HStack gap={3} align="center">
                                    <Box
                                        bg="orange.50"
                                        color="orange.500"
                                        w="44px"
                                        h="44px"
                                        borderRadius="full"
                                        display="grid"
                                        placeItems="center"
                                    >
                                        <Icon as={FiLock} boxSize={5} />
                                    </Box>
                                    <VStack align="stretch" gap={0}>
                                        <Text fontWeight={700} fontSize="lg" lineHeight={1.2}>
                                            {titleFor(payload, t as unknown as TFn)}
                                        </Text>
                                        {payload.planName && (
                                            <Text color="gray.500" fontSize="sm">
                                                {t('limitGate.currentPlan', {
                                                    plan: payload.planName,
                                                    defaultValue: `You're on ${payload.planName}`,
                                                })}
                                            </Text>
                                        )}
                                    </VStack>
                                </HStack>

                                <Text color="gray.700" lineHeight={1.55}>
                                    {bodyFor(payload, t as unknown as TFn)}
                                </Text>

                                {payload.suggestedPlan && (
                                    <Box
                                        bg="blue.50"
                                        border="1px solid"
                                        borderColor="blue.200"
                                        borderRadius="12px"
                                        p={3}
                                    >
                                        <HStack gap={2}>
                                            <Icon as={FiTrendingUp} color="blue.600" boxSize={4} />
                                            <Text fontSize="sm" color="blue.700">
                                                {t('limitGate.suggestion', {
                                                    plan: planLabel(payload.suggestedPlan),
                                                    defaultValue: `Available on ${planLabel(payload.suggestedPlan)} and up`,
                                                })}
                                            </Text>
                                        </HStack>
                                    </Box>
                                )}

                                <HStack gap={2} justify="flex-end" pt={1}>
                                    <Button variant="ghost" onClick={close}>
                                        {t('limitGate.notNow', {defaultValue: 'Not now'})}
                                    </Button>
                                    <Button colorPalette="blue" onClick={upgrade}>
                                        {t('limitGate.upgrade', {defaultValue: 'Upgrade plan'})}
                                    </Button>
                                </HStack>
                            </VStack>
                            )}
                        </Dialog.Body>
                    </Dialog.Content>
                </Dialog.Positioner>
            </Portal>
        </Dialog.Root>
    )
}

function planLabel(p: string): string {
    return p.charAt(0).toUpperCase() + p.slice(1)
}

type TFn = (key: string, opts: Record<string, unknown>) => string
function titleFor(p: GatePayload, t: TFn): string {
    return t(`limitGate.title.${p.key}`, { defaultValue: defaultTitle(p) })
}
function bodyFor(p: GatePayload, t: TFn): string {
    if (p.reason) return p.reason
    return t(`limitGate.body.${p.key}`, {
        used: p.used,
        limit: p.limit,
        defaultValue: defaultBody(p),
    })
}

function defaultTitle(p: GatePayload): string {
    switch (p.key) {
        case 'staff':
            return 'Add another barber?'
        case 'bookings':
            return 'Booking limit reached'
        case 'sms':
            return 'SMS quota reached'
        case 'vacations':
            return 'Vacation management is a paid feature'
        case 'waitlist':
            return 'Smart Waitlist is a paid feature'
        case 'multiServices':
            return 'Multiple services is a paid feature'
        case 'reminder1h':
            return '1-hour reminders are a paid feature'
        case 'remindersCustom':
            return 'Custom reminders are a paid feature'
        case 'customDomain':
            return 'Custom domain is a paid feature'
        case 'eshop':
            return 'E-shop is a paid feature'
        case 'whiteLabel':
            return 'White-label branding is a paid feature'
        case 'featured':
            return 'Featured listing is a paid feature'
    }
}

function defaultBody(p: GatePayload): string {
    const usedLimit =
        p.used !== undefined && p.limit !== undefined
            ? ` (${p.used}/${p.limit})`
            : ''
    switch (p.key) {
        case 'staff':
            return `You've reached the seat limit on your current plan${usedLimit}. Upgrade to add more barbers to your shop.`
        case 'bookings':
            return `You've hit the monthly booking limit${usedLimit}. Upgrade to keep accepting reservations this cycle.`
        case 'sms':
            return `You've used your SMS allowance${usedLimit}. Upgrade or top up to keep sending text confirmations.`
        case 'vacations':
            return 'Block vacation days and manage time off on the calendar — available on paid plans.'
        case 'waitlist':
            return 'Auto-fill cancellations from a waitlist and notify customers when slots open up.'
        case 'multiServices':
            return 'Offer more than one service from your booking page — available on paid plans.'
        case 'reminder1h':
            return 'Send a 1-hour reminder before each appointment to cut no-shows.'
        case 'remindersCustom':
            return 'Pick your own reminder times (e.g. 24h + 2h before).'
        case 'customDomain':
            return 'Use your own domain (e.g. book.yourshop.com) instead of the BarberMate URL.'
        case 'eshop':
            return 'Sell products directly from your booking page.'
        case 'whiteLabel':
            return 'Hide BarberMate branding so the booking page looks fully yours.'
        case 'featured':
            return 'Promote your shop to the top of the Explore page in your city.'
    }
}

/**
 * Inline lock chip — drop next to any CTA to telegraph "your plan can't do
 * this" before the user clicks. Aria-hidden because the modal it leads to
 * carries the actual semantics.
 */
export function LockBadge({label}: {label?: string}) {
    return (
        <HStack
            gap="3px"
            display="inline-flex"
            bg="orange.50"
            color="orange.600"
            px="6px"
            py="2px"
            borderRadius="full"
            fontSize="10px"
            fontWeight={700}
            letterSpacing="0.06em"
            textTransform="uppercase"
            aria-hidden
        >
            <Icon as={FiLock} boxSize="10px" />
            {label && <Text as="span">{label}</Text>}
        </HStack>
    )
}
